import { Box, Dialog, Typography } from '@mui/material'
import OfoqThemeBtn from '../../components/Buttons/OfoqThemeBtn'
import tokens, { keyframes } from './tokens'
import {
  AlertIcon, BellIcon, FaceIcon, FingerprintIcon,
} from './icons'

// icon key -> [component, circle tint]. The prototype tints the circle amber for
// the "about to do something" prompts and red for failures and destructive ones.
const ICONS = {
  fingerprint: [FingerprintIcon, tokens.color.amberLight],
  face: [FaceIcon, tokens.color.amberLight],
  alert: [AlertIcon, tokens.color.redLight],
  bell: [BellIcon, tokens.color.amberLight],
}

/**
 * The prototype's dialog: optional titled header, an 88px tinted circle holding
 * the icon, centred body copy, and a footer of text + filled buttons.
 *
 * `children` renders under the body text - used by the enable flow for the
 * password field, the one element the prototype does not have because it never
 * had to prove who was holding the phone.
 */
const PromptDialog = ({
  open,
  icon,
  title,
  body,
  confirmText,
  cancelText,
  danger,
  loading,
  confirmDisabled,
  onConfirm,
  onCancel,
  children,
}) => {
  const [Icon, tint] = ICONS[icon] || []

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      PaperProps={{ sx: styles.paper }}
      BackdropProps={{ sx: styles.backdrop }}
    >
      {title && (
        <Box sx={styles.header}>
          <Typography sx={styles.title}>{title}</Typography>
        </Box>
      )}

      <Box sx={styles.body}>
        {Icon && (
          <Box sx={[styles.iconCircle, { backgroundColor: tint }]}>
            <Icon />
          </Box>
        )}

        <Typography sx={styles.bodyText}>{body}</Typography>

        {children}
      </Box>

      {/* The app's own button, so these keep the shared hover animation and
          Avenir-bold face; the overrides only carry the prototype's metrics and
          the red confirm the shared component has no variant for. */}
      <Box sx={styles.footer}>
        <OfoqThemeBtn
          name={cancelText}
          disabled={loading}
          onClick={onCancel}
          customStyle={[styles.button, styles.textButton]}
        />
        <OfoqThemeBtn
          name={confirmText}
          disabled={loading || confirmDisabled}
          onClick={onConfirm}
          customStyle={[styles.button, danger && styles.dangerButton]}
        />
      </Box>
    </Dialog>
  )
}

const styles = {
  backdrop: {
    backgroundColor: tokens.color.scrim,
  },
  paper: {
    ...keyframes,
    width: '100%',
    // 22px of breathing room each side, as the prototype insets its dialog.
    margin: '22px',
    maxWidth: 'calc(100% - 44px)',
    borderRadius: tokens.radius.dialog,
    boxShadow: tokens.shadow.dialog,
    backgroundColor: tokens.color.white,
    overflow: 'hidden',
    animation: `settingsPop ${tokens.duration.base} ${tokens.ease.standard}`,
  },
  header: {
    padding: '18px 22px',
    borderBottom: `1px solid ${tokens.color.border}`,
  },
  title: {
    fontSize: '17px',
    fontFamily: tokens.fontBold,
    color: tokens.color.textPrimary,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '18px',
    padding: '26px 24px',
    textAlign: 'center',
  },
  iconCircle: {
    width: '88px',
    height: '88px',
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
  },
  bodyText: {
    fontSize: '14px',
    lineHeight: 1.7,
    color: tokens.color.textSecondary,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    padding: '14px 18px',
    borderTop: `1px solid ${tokens.color.border}`,
  },
  button: {
    height: '40px',
    width: 'auto',
    minWidth: 'unset',
    maxWidth: 'none',
    // OfoqThemeBtn spaces itself with marginInline; the footer owns the 10px gap.
    marginInline: 0,
    padding: '0 18px',
    borderRadius: tokens.radius.control,
    fontSize: '14px',
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': { boxShadow: 'none' },
    '&.Mui-disabled': { backgroundColor: tokens.color.border, color: tokens.color.white },
  },
  // The prototype's cancel is a plain text button, not the app's tinted one.
  textButton: {
    color: tokens.color.textSecondary,
    backgroundColor: 'transparent',
    '&:hover': { backgroundColor: tokens.color.surfaceBand, color: tokens.color.textSecondary },
  },
  dangerButton: {
    backgroundColor: tokens.color.red,
    '&:hover': { backgroundColor: tokens.color.redDark },
  },
}

export default PromptDialog
